import React from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import cond from 'lodash/cond';
import constant from 'lodash/constant';
import HyperLink from 'common/icons/VirtualEmbed';

import { useAttendeeSessions } from 'common/components/TouchpointEditor/contexts/AttendeeSessionContext';
import useSessionsRoutes from 'common/components/TouchpointEditor/hooks/useSessionsRoutes';
import { EmbedPlaceholder } from '../../../../components/Common.styles';
import CustomEmbed from '../../../../components/elements/Embed/CustomEmbed';
import VimeoEmbed from '../../../../components/elements/Embed/VimeoEmbed';
import ZoomEmbed from '../../../../components/elements/Embed/ZoomEmbed';
import IntrovokeEmbed from '../../../../components/elements/Embed/IntrovokeEmbed';

import { EmbedContainer, EmbedRow } from './Embed.styles';

const EMBED_TYPES = {
    ZOOM: 'zoom',
    CUSTOM: 'custom',
    VIMEO: 'vimeo',
    INTROVOKE: 'splashStudio',
};

const Embed = ({ showPlaceholder, guest }) => {
    const attendeeSessions = useAttendeeSessions();
    const embeds = get(attendeeSessions, 'activeSession.virtualVenue.embeds', []);
    const primaryEmbed = embeds.find((session) => session.order === 1);
    const secondaryEmbed = embeds.find((session) => session.order === 2);
    const history = useHistory();
    const sessionsRoutes = useSessionsRoutes();

    const Placeholder = (
        <EmbedPlaceholder>
            <HyperLink />
            Virtual Content
        </EmbedPlaceholder>
    );

    const getEmbedComponent = (embed = {}) =>
        cond([
            [constant(showPlaceholder), constant(Placeholder)],
            [
                // Custom Embed
                constant(EMBED_TYPES.CUSTOM === embed.type),
                () => <CustomEmbed embedCode={embed.options.custom.embedCode} />,
            ],
            [
                // Vimeo Embed (includes Chat)
                constant(EMBED_TYPES.VIMEO === embed.type),
                () => (
                    <VimeoEmbed videoId={embed.options.vimeo.videoId} supportsChat={embed.options.vimeo.supportsChat} />
                ),
            ],
            [
                // Zoom Embed
                constant(EMBED_TYPES.ZOOM === embed.type),
                () => (
                    <ZoomEmbed
                        guest={guest}
                        meetingId={embed.options.zoom.meetingId}
                        signature={embed.options.zoom.signature}
                        password={embed.options.zoom.meetingPassword}
                        features={{
                            showGuestEmails: embed.options.zoom.showGuestEmailsEnabled,
                            supportsChat: embed.options.zoom.supportsChat,
                        }}
                        onLeaveMeeting={() => {
                            history.replace(sessionsRoutes.leaveSessionUrl);
                        }}
                    />
                ),
            ],
            [
                // Splash Studio Embed
                constant(EMBED_TYPES.INTROVOKE === embed.type),
                () => (
                    <IntrovokeEmbed
                        introvokeEventId={embed.options.splashStudio.meetingId}
                        userToken={embed.options.splashStudio.userToken}
                        shouldRefreshEmbeds={false}
                    />
                ),
            ],
            [constant(true), constant(Placeholder)],
        ])();

    return (
        <EmbedContainer>
            <EmbedRow flex={3}>{getEmbedComponent(primaryEmbed)}</EmbedRow>
            {secondaryEmbed && <EmbedRow flex={1}>{getEmbedComponent(secondaryEmbed)}</EmbedRow>}
        </EmbedContainer>
    );
};

Embed.propTypes = {
    showPlaceholder: PropTypes.bool,
    guest: PropTypes.object,
};

Embed.defaultProps = {
    showPlaceholder: false,
    guest: {},
};

export default Embed;
